// src/components/trial/lp/trial-beta/sections/final-cta.tsx
// Ported from the design attachment, markup lines 4096-4118. The source's button carries
// `data-open-trial-modal`, picked up by a global click delegate that opens the signup modal;
// Task 12 owns that modal, so here the button just calls the `onOpenModal` prop it is given.
//
// The note line (`<i>✓</i> 1 day free <span>·</span> No credit card required`) keeps the
// source's exact elements — the checkmark `<i>` and the middot `<span>` both still need to exist
// for `.np-final-action > p i` and `> p span` to resolve — so `final_cta.note` carries the whole
// sentence, tags included, as one `_one`/`_other` translation, rendered through `Trans` per this
// repo's own convention ("Use `Trans` component for strings with embedded HTML/components").
// `{{count}}` is interpolated by `t()` before `Trans` ever sees the string, so the trial length
// still comes from the prop, never a hardcoded 1.
import { Trans, useTranslation } from 'next-i18next';

interface FinalCtaProps {
  daysTrial: number;
  onOpenModal: () => void;
}

// Values are counts, not language content — kept literal the same way hero.tsx hardcodes its own
// stat pills and only translates their labels.
const PROOF_ITEMS = [
  { value: '3M+', labelKey: 'final_cta.proof.players' },
  { value: '2,000', labelKey: 'final_cta.proof.servers' },
  { value: '150+', labelKey: 'final_cta.proof.countries' },
] as const;

export function FinalCta({ daysTrial, onOpenModal }: FinalCtaProps) {
  const { t } = useTranslation('components/trial/lp/trial-beta');

  return (
    <section id='final-cta' data-anim='cta-final' className='relative overflow-hidden px-4 py-32'>
      <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-[#93FF18]/5 via-transparent to-transparent' />
      <div className='pointer-events-none absolute left-1/2 top-1/2 size-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2ECDFF]/5 blur-[150px]' />
      <div className='relative mx-auto max-w-6xl'>
        <div className='np-final-command'>
          <div className='np-final-copy'>
            <span>
              <i aria-hidden='true' /> {t('final_cta.eyebrow')}
            </span>
            <h2 className='text-4xl font-bold md:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
              {t('final_cta.title')} <span>{t('final_cta.titleHighlight')}</span>
            </h2>
            <p>{t('final_cta.description')}</p>
          </div>
          <div className='np-final-action'>
            <button type='button' onClick={onOpenModal}>
              <span>{t('final_cta.button')}</span>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M5 12h13M13 6l6 6-6 6' />
              </svg>
            </button>
            <p data-testid='final-cta-note'>
              <Trans components={{ i: <i aria-hidden='true' />, span: <span aria-hidden='true' /> }}>{t('final_cta.note', { count: daysTrial })}</Trans>
            </p>
          </div>
          <div className='np-final-proof' aria-label={t('final_cta.proofAriaLabel')}>
            {PROOF_ITEMS.map((proofItem) => (
              <div key={proofItem.labelKey}>
                <b>{proofItem.value}</b>
                <span>{t(proofItem.labelKey)}</span>
              </div>
            ))}
            <div className='np-final-network'>
              <i aria-hidden='true' />
              <b>{t('final_cta.proof.networkStatus')}</b>
              <span>{t('final_cta.proof.network')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
